import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiUpload, FiEdit2, FiDownload, FiMail, FiSend, FiTrash2 } from "react-icons/fi";
import {
  getEmployees, filterEmployees, getLookups, createEmployee, updateEmployee, updateEmployeeStatus,
} from "../../services/employeeApi";
import useAsync from "../../hooks/useAsync";
import { useRole } from "../../context/RoleContext";
import { Card, PageHeader, Button, DataTable, PersonCell, StatusBadge, SearchInput, ConfirmDialog, ErrorState, FormField, Banner, Modal, OrgFilters, MultiSelect } from "../../components/common";
import EmployeeFormModal from "./components/EmployeeFormModal";
import ReportingResolutionsModal from "./components/ReportingResolutionsModal";
import { canResolveReporting, listReportingResolutions } from "../../services/reportingResolutionApi";
import ImportWizardModal from "../../components/imports/ImportWizardModal";
import InviteLoginModal from "../../components/invitations/InviteLoginModal";
import InvitationsTable from "../../components/invitations/InvitationsTable";
import BulkInviteModal from "../../components/invitations/BulkInviteModal"; // [NEW] select-all / bulk invite
import BulkSelectionBar from "../../components/invitations/BulkSelectionBar";
import { exportEmployeeMaster } from "../../services/bulkImportApi";
import { canInvite, listInvitations } from "../../services/invitationApi";
import { deactivateEmployee, describeDeactivation, canManageLogins, canDeleteEmployee } from "../../services/employeeLifecycleApi";
import DeleteEmployeeDialog from "../../components/employees/DeleteEmployeeDialog"; // [NEW] delete employee
import { setAccountActive } from "../../services/accountApi";
import { EMPLOYMENT_STATUSES } from "../../utils/constants";
import { ROLE_LABELS } from "../../services/navigation";
import { formatDate } from "../../utils/format";
import useOrgFilters from "../../hooks/useOrgFilters"; // [NEW] shared Business Unit / Department / Location / Cost Centre / Legal Entity filters
import { downloadCsv } from "../../utils/exportCsv";
import { downloadXlsx } from "../../utils/exportExcel";
import { getErrorMessage, getErrorCode } from "../../utils/errors";
import Select from "../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { getEmployees, getLookups, createEmployee, updateEmployee, updateEmployeeStatus, exportEmployeeMaster, deactivateEmployee, setAccountActive };

export default function EmployeeMasterPage() {
  const { profile } = useRole();
  // [UPDATED] PM-18: status, role and "reports to" hold lists (checkbox dropdowns).
  const [filters, setFilters] = useState({ search: "", status: [], role: [], managerId: [] });
  const lookups = useAsync(() => api.getLookups(), []);
  const [version, setVersion] = useState(0);
  const all = useAsync(() => api.getEmployees({}, { fresh: version > 0 }), [version]);
  // [NEW] Business Unit, Department, Location, Cost Centre and Legal Entity filters (options come from the employee list itself)
  const org = useOrgFilters({ list: all.data ?? null });
  const rows = useMemo(() => filterEmployees(all.data || [], filters).filter((e) => org.matches(e)), [all.data, filters, org.matches]); // eslint-disable-line react-hooks/exhaustive-deps
  const rolesKnown = (all.data || []).some((e) => e.rolesKnown);
  const canEditRoles = (profile?.grants || []).some((g) => g.permission_code === "rbac.user_role.grant");
  const [editing, setEditing] = useState(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [invitationsOpen, setInvitationsOpen] = useState(false);
  const [invitationsFilter, setInvitationsFilter] = useState("all"); // [NEW] "failed" opens the list on failed emails
  const [invitationsVersion, setInvitationsVersion] = useState(0);
  const [selected, setSelected] = useState(() => new Set()); // [NEW] ticked employee IDs
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState("");
  const permissions = useAsync(async () => ({ invite: await canInvite(), logins: await canManageLogins(), resolve: await canResolveReporting(), remove: await canDeleteEmployee() }), []);
  const [deleting, setDeleting] = useState(null); // [NEW] employee row being deleted
  // [NEW] Invitation list shared with the "Login invitations" window, so the failed count is always current.
  const invitations = useAsync(() => listInvitations(), [invitationsVersion, permissions.data?.invite], { skip: !permissions.data?.invite });
  const failedInvites = (invitations.data || []).filter((i) => i.status === "DELIVERY_FAILED").length;
  // [NEW] PM-1: everyone who can be invited, has never had an invitation and (when the backend says so) has no login yet, for "Invite all not invited" (one attempt).
  const invitedIds = useMemo(() => new Set((invitations.data || []).map((i) => i.employeeId)), [invitations.data]);
  const openInvitations = (filter) => { setInvitationsFilter(filter); setInvitationsOpen(true); };
  // [NEW] Pending reporting-manager names from employee imports (GET /employee-reporting-resolutions).
  const [resolutionsOpen, setResolutionsOpen] = useState(false);
  const pendingResolutions = useAsync(() => listReportingResolutions("PENDING"), [version, permissions.data?.resolve], { skip: !permissions.data?.resolve });
  const pendingCount = pendingResolutions.data?.length || 0;
  // [NEW] "Reports to" column helpers: unmatched manager names from imports, and the list of managers for the filter.
  const unmatchedByEmployee = useMemo(() => Object.fromEntries((pendingResolutions.data || []).map((r) => [r.employeeId, r])), [pendingResolutions.data]);
  const managerOptions = useMemo(() => {
    const ids = new Set((all.data || []).map((e) => e.managerId).filter(Boolean));
    return [...ids].map((id) => ({ id, name: (all.data || []).find((e) => e.id === id)?.name || id }));
  }, [all.data]);

  // [NEW] Active employees other than the signed-in user can be invited. People who already have a login are skipped by the server.
  const canInviteRow = (r) => Boolean(permissions.data?.invite) && r.status === "Active" && r.id !== profile.id && r.hasLogin !== true; // [FIXED] already invited / has a login -> use Login invitations to resend
  const selectedRows = useMemo(() => rows.filter((r) => canInviteRow(r) && selected.has(r.id)), [rows, selected, permissions.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const notInvited = useMemo(() => (all.data || []).filter((r) => canInviteRow(r) && !invitedIds.has(r.id) && r.hasLogin !== true), [all.data, invitedIds, permissions.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [bulkAll, setBulkAll] = useState(false);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const refresh = () => { setVersion((v) => v + 1); lookups.reload(); };

  const save = async (form) => {
    try {
      const { data } = editing ? await api.updateEmployee(editing.id, form) : await api.createEmployee(form);
      toast.success(editing ? "Employee updated." : "Employee added. Click Invite on their row to email their login link.");
      if (data?.warning) toast.info(data.warning);
      refresh();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return false;
    }
  };

  const openStatus = (row) => setStatusTarget({ ...row, replacementId: "", reason: "", error: "", newStatus: "Inactive" });

  const reactivate = async () => {
    await api.updateEmployeeStatus(statusTarget.id, "Active", { reason: statusTarget.reason.trim() || undefined });
    let login = "An administrator must allow their login again (Users & roles).";
    if (permissions.data?.logins) {
      try { await api.setAccountActive(statusTarget.id, true, "Employee re-activated"); login = "Their login is allowed again."; }
      catch (err) { login = getErrorCode(err) === "ACCOUNT_NOT_FOUND" ? "They have no login yet." : `Their login could not be re-opened: ${getErrorMessage(err)}`; }
    }
    toast.success(`${statusTarget.name} is active again. ${login} Re-include excluded appraisals from Review & finalize if needed.`);
  };

  const toggleStatus = async () => {
    const next = statusTarget.status === "Active" ? "Inactive" : "Active";
    const needsReplacement = next !== "Active" && statusTarget.directReportCount > 0;
    if (needsReplacement && !statusTarget.replacementId) {
      setStatusTarget((t) => ({ ...t, error: "Pick who takes over their team and reviews." }));
      return;
    }
    setBusy(true);
    try {
      if (next === "Active") {
        await reactivate();
      } else {
        const { data } = await api.deactivateEmployee(statusTarget.id, {
          status: statusTarget.newStatus,
          replacementManagerId: statusTarget.replacementId || undefined,
          reason: statusTarget.reason.trim() || undefined,
        });
        toast.success(`${describeDeactivation(statusTarget.name, data.deactivation)}${statusTarget.replacementId ? ` Their team and open reviews moved to ${statusTarget.replacementName || statusTarget.replacementId}.` : ""}`);
      }
      setStatusTarget(null);
      refresh();
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const message = code === "REPLACEMENT_REQUIRED"
        ? `${statusTarget.name} still reviews someone's appraisal. Pick a replacement manager below and try again.`
        : getErrorMessage(err);
      setStatusTarget((t) => (t ? { ...t, error: message, needsPick: code === "REPLACEMENT_REQUIRED" || t.needsPick } : t));
    } finally {
      setBusy(false);
    }
  };
  const replacementOptions = (all.data || []).filter((e) => e.status === "Active" && e.id !== statusTarget?.id);

  const exportMaster = async (format) => {
    setExporting(format);
    try {
      // The server export knows one status only; with other filters the file is built from the rows on screen.
      if (org.active || filters.status.length > 1 || filters.role.length || filters.managerId.length) {
        // [NEW] The server export does not know the new filters, so build the file from the rows shown on screen.
        const cols = [
          { key: "id", label: "Employee ID" }, { key: "name", label: "Name" }, { key: "email", label: "Email" },
          { key: "businessUnit", label: "Business Unit" }, { key: "departmentName", label: "Department" },
          { key: "designationName", label: "Designation" }, { key: "location", label: "Location" },
          { key: "costCenter", label: "Cost Centre" }, { key: "legalEntity", label: "Legal Entity" },
          { key: "mobilePhone", label: "Mobile phone" }, { key: "currentAddress", label: "Current address" },
          { key: "managerName", label: "Reports to" }, { key: "dateOfJoining", label: "Joined" }, { key: "status", label: "Status" },
        ];
        if (format === "csv") downloadCsv("employees-filtered", cols, rows);
        else downloadXlsx("employees-filtered", [{ name: "Employees", title: "Employees (filtered)", columns: cols, rows }]);
      } else {
        await api.exportEmployeeMaster({ status: filters.status[0] || "", search: filters.search.trim() }, format);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting("");
    }
  };

  if (all.error) return <ErrorState message={all.error} onRetry={all.reload} />;

  return (
    <div className="page">
      <PageHeader
        title="Employees"
        subtitle="Employee records, reporting lines and application roles."
        actionsOneLine
        actions={
          <>
            <Button variant="ghost" icon={FiDownload} loading={exporting === "csv"} onClick={() => exportMaster("csv")}>CSV</Button>
            <Button variant="ghost" icon={FiDownload} loading={exporting === "xlsx"} onClick={() => exportMaster("xlsx")}>Excel</Button>
            {permissions.data?.invite && invitations.data && notInvited.length > 0 && (
              <Button variant="secondary" icon={FiSend} onClick={() => setBulkAll(true)} title="Send a login invitation to every active employee who has not been invited yet">Invite all not invited ({notInvited.length})</Button>
            )}
            {permissions.data?.invite && <Button variant="ghost" icon={FiMail} onClick={() => openInvitations(failedInvites ? "failed" : "all")}>Login invitations{failedInvites ? ` (${failedInvites} failed)` : ""}</Button>}
            {/* [UPDATED] "Unmatched managers" button removed; open that list from the warning banner ("Review")
                or the "Needs review" tag in the Reports to column. */}
            <Button variant="secondary" icon={FiUpload} onClick={() => setImportOpen(true)}>Import CSV / Excel</Button>
            <Button icon={FiPlus} onClick={() => setEditing(null)}>Add employee</Button>
          </>
        }
      />

      {permissions.data?.invite && failedInvites > 0 && (
        <Banner
          tone="danger"
          title={`${failedInvites} login ${failedInvites === 1 ? "invitation email" : "invitation emails"} could not be delivered`}
          action={<Button size="sm" variant="secondary" onClick={() => openInvitations("failed")}>View failed</Button>}
        >
          Open the failed list to resend them, one by one or all at once.
        </Banner>
      )}

      {pendingCount > 0 && (
        <Banner
          tone="warning"
          title={`${pendingCount} imported ${pendingCount === 1 ? "employee has" : "employees have"} no confirmed reporting manager`}
          action={<Button size="sm" variant="secondary" onClick={() => setResolutionsOpen(true)}>Review</Button>}
        >
          The import file named a manager that matched nobody or more than one person. Confirm the right manager so they appear in that manager&apos;s team.
        </Banner>
      )}

      <div className="filter-bar">
        <SearchInput value={filters.search} onChange={setFilter("search")} placeholder="Search ID, name or email" />
        {/* [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity */}
        <OrgFilters org={org} showClear={false} />
        <MultiSelect label="Status" allLabel="Any status" keepOrder options={EMPLOYMENT_STATUSES.map((st) => ({ value: st, label: st }))} value={filters.status} onChange={setFilter("status")} />
        {rolesKnown && <MultiSelect label="Role" allLabel="Any role" options={Object.entries(ROLE_LABELS).map(([k, v]) => ({ value: k, label: v }))} value={filters.role} onChange={setFilter("role")} />}
        {/* Filter by reporting manager */}
        <MultiSelect label="Reports to" allLabel="Reports to: anyone" pinned={[{ value: "__none__", label: "No manager" }]} options={managerOptions.map((m) => ({ value: m.id, label: `${m.name} (${m.id})` }))} value={filters.managerId} onChange={setFilter("managerId")} />
        {(org.active || filters.status.length > 0 || filters.role.length > 0 || filters.managerId.length > 0) && (
          <Button variant="ghost" size="sm" onClick={() => { org.clear(); setFilters((f) => ({ ...f, status: [], role: [], managerId: [] })); }}>Clear filters</Button>
        )}
      </div>

      <BulkSelectionBar count={selectedRows.length} onClear={() => setSelected(new Set())} onInvite={() => setBulkOpen(true)} />
      <Card padded={false}>
        <DataTable exportable={false}
          loading={all.loading && !all.data}
          rows={rows}
          selection={permissions.data?.invite ? { selected, onChange: setSelected, isSelectable: canInviteRow, label: "active employees" } : undefined}
          emptyTitle="No employees match"
          emptyText="Try a different search or clear the filters."
          columns={[
            { key: "name", label: "Employee", render: (r) => <PersonCell name={r.name} meta={<>{r.id}{r.email && <><br />{r.email}</>}</>} /> },
            // [NEW] Business Unit, Department, Location, Cost Centre, Legal Entity, Mobile phone, Current address
            { key: "businessUnit", label: "Business Unit", render: (r) => r.businessUnit || <span className="muted">—</span> },
            { key: "department", label: "Department", render: (r) => r.departmentName || "—" },
            { key: "designation", label: "Designation", render: (r) => (r.designationName ? <span className="cell-clamp cell-clamp-2" title={r.designationName}>{r.designationName}</span> : "—") },
            { key: "location", label: "Location", render: (r) => (r.location ? <span className="cell-clamp cell-clamp-3" title={r.location}>{r.location}</span> : <span className="muted">—</span>) },
            { key: "costCenter", label: "Cost Centre", render: (r) => r.costCenter || <span className="muted">—</span> },
            { key: "legalEntity", label: "Legal Entity", render: (r) => r.legalEntity || <span className="muted">—</span> },
            { key: "mobilePhone", label: "Mobile phone", render: (r) => (r.mobilePhone ? <span className="cell-oneline">{r.mobilePhone}</span> : <span className="muted">—</span>) },
            { key: "currentAddress", label: "Current address", render: (r) => (r.currentAddress ? <span className="cell-clamp cell-clamp-3 cell-address" title={r.currentAddress}>{r.currentAddress}</span> : <span className="muted">—</span>) },
            {
              // [UPDATED] Reports to: manager name + employee ID; flags imported names that still need HR review.
              key: "managerName", label: "Reports to",
              render: (r) => {
                if (r.managerId) return <span className="cell-stack cell-person-lite"><span className="cell-clamp cell-clamp-2" title={r.managerName}>{r.managerName}</span><small>{r.managerId}</small></span>;
                const unmatched = unmatchedByEmployee[r.id];
                if (unmatched) {
                  return (
                    <button type="button" className="tag tag-warning reports-to-review" onClick={() => setResolutionsOpen(true)} title="The manager name in the import file matched nobody or several people. Click to choose the manager.">
                      Needs review: {unmatched.managerNameInFile}
                    </button>
                  );
                }
                return <span className="muted">Not assigned</span>;
              },
            },
            { key: "doj", label: "Joined", render: (r) => formatDate(r.dateOfJoining) },
            {
              key: "role", label: "Roles",
              render: (r) => (r.rolesKnown
                ? <span className="role-tags">{(r.roles.length ? r.roles : ["employee"]).map((k) => <span key={k} className="tag">{ROLE_LABELS[k] || k}</span>)}</span>
                : <span className="muted" title="Only administrators can see application roles (Users & roles).">—</span>),
            },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            {
              key: "actions", label: "Action", 
              render: (r) => (
                <span className="row-actions">
                  {permissions.data?.invite && r.status === "Active" && r.id !== profile.id && (
                    <Button variant="ghost" size="sm" icon={FiSend} title="Create their login and email a password setup link" onClick={() => setInviting(r)}>Invite</Button>
                  )}
                  {r.id !== profile.id && (
                    <Button variant="ghost" size="sm" onClick={() => openStatus(r)}>
                      {r.status === "Active" ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                  <span className="row-icons">
                    <Button variant="ghost" size="sm" icon={FiEdit2} aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)} />
                    {permissions.data?.remove && r.id !== profile.id && (
                      <Button variant="ghost" size="sm" icon={FiTrash2} aria-label={`Delete ${r.name}`} title="Permanently delete (only if they have no records)" onClick={() => setDeleting(r)} />
                    )}
                  </span>
                </span>
              ),
            },
          ]}
        />
      </Card>

      <EmployeeFormModal
        open={editing !== undefined}
        employee={editing}
        employees={all.data || []}
        lookups={lookups.data}
        canEditRoles={canEditRoles}
        onClose={() => setEditing(undefined)}
        onSave={save}
      />
      <ImportWizardModal open={importOpen} type="EMPLOYEE" onClose={() => setImportOpen(false)} onImported={refresh} />
      <ReportingResolutionsModal
        open={resolutionsOpen}
        managers={lookups.data?.managers || []}
        onClose={() => setResolutionsOpen(false)}
        onChanged={refresh}
      />
      <BulkInviteModal
        employees={bulkAll ? notInvited : null}
        onClose={() => setBulkAll(false)}
        onFinished={() => setInvitationsVersion((v) => v + 1)}
      />
      <BulkInviteModal
        employees={bulkOpen ? selectedRows : null}
        onClose={() => setBulkOpen(false)}
        onFinished={() => { setSelected(new Set()); setInvitationsVersion((v) => v + 1); refresh(); }}
      />
      <DeleteEmployeeDialog
        employee={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(emp) => { setDeleting(null); setSelected((prev) => { const next = new Set(prev); next.delete(emp.id); return next; }); refresh(); }}
        onDeactivate={(emp) => { setDeleting(null); openStatus(emp); }}
      />
      <InviteLoginModal employee={inviting} onClose={() => setInviting(null)} onSent={() => setInvitationsVersion((v) => v + 1)} />
      <Modal open={invitationsOpen} size="lg" title="Login invitations" subtitle="Latest invitation per employee. Resend if the link expired or the email failed." onClose={() => setInvitationsOpen(false)}>
        <InvitationsTable version={invitationsVersion} source={invitations} filter={invitationsFilter} onFilterChange={setInvitationsFilter} />
      </Modal>
      <ConfirmDialog
        open={Boolean(statusTarget)}
        title={statusTarget?.status === "Active" ? `Deactivate ${statusTarget?.name}?` : `Activate ${statusTarget?.name}?`}
        message={statusTarget?.status === "Active"
          ? "Their login is disabled at once, open appraisals are excluded (no new goals, KRAs or KPIs) and pending KRA/KPI proposals are withdrawn. Finalized results and history are kept."
          : "They become active again. Their login and any excluded appraisals are re-opened separately."}
        confirmLabel={statusTarget?.status === "Active" ? "Deactivate" : "Activate"}
        tone={statusTarget?.status === "Active" ? "danger" : "primary"}
        loading={busy}
        onConfirm={toggleStatus}
        onClose={() => setStatusTarget(null)}
      >
        {statusTarget?.status === "Active" && (
          <div className="form-stack">
            <FormField label="New status">
              <Select value={statusTarget.newStatus} onChange={(e) => setStatusTarget((t) => ({ ...t, newStatus: e.target.value, error: "" }))}>
                <option value="Inactive">Inactive (for example long leave)</option>
                <option value="Exited">Exited (left the company today)</option>
              </Select>
            </FormField>
            {statusTarget.directReportCount > 0 ? (
              <Banner tone="warning" title={`${statusTarget.name} manages ${statusTarget.directReportCount} ${statusTarget.directReportCount === 1 ? "person" : "people"}`}>
                {statusTarget.directReports.map((d) => d.name).join(", ")} will report to the replacement you pick, and every
                appraisal {statusTarget.name} is reviewing (not yet finalized) moves to that person too.
              </Banner>
            ) : (
              <p className="form-hint">
                If {statusTarget.name} is reviewing anyone&apos;s appraisal, pick who takes over those reviews.
              </p>
            )}
            <FormField
              label="Replacement manager"
              required={statusTarget.directReportCount > 0 || statusTarget.needsPick}
              error={statusTarget.error || undefined}
            >
              <Select
                value={statusTarget.replacementId}
                onChange={(e) => {
                  const pick = replacementOptions.find((x) => x.id === e.target.value);
                  setStatusTarget((t) => ({ ...t, replacementId: e.target.value, replacementName: pick?.name, error: "" }));
                }}
              >
                <option value="">{statusTarget.directReportCount > 0 ? "Choose a replacement" : "No replacement needed"}</option>
                {replacementOptions.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
              </Select>
            </FormField>
            <FormField label="Reason (optional)" hint="Saved in the audit trail.">
              <input value={statusTarget.reason} onChange={(e) => setStatusTarget((t) => ({ ...t, reason: e.target.value }))} />
            </FormField>
          </div>
        )}
        {statusTarget && statusTarget.status !== "Active" && statusTarget.error && <p className="form-error">{statusTarget.error}</p>}
      </ConfirmDialog>
    </div>
  );
}
